import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { ISiteSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ServerSettings, ServerSettingsInstance } from '../../../settings/services/ServerSettings';
import { Sites } from '../../../ts/resources/Sites';
import { IGetSiteSettingsForSiteAdminResult } from '../../../ts/ServerApi/requests/GetSiteSettingsForSiteAdminRequest';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../ts/services/SiteAuthenticationSettingsService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

const template = `<div ng-controller="SiteAuthenticationSettingsCtrl" ng-include="'siteSettingsAuthentication.html'"></div>`;

export interface AuthenticationSiteSettingsSubspaceOwnProps extends ReactDirectiveProps{
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface AuthenticationSiteSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
  readonly siteAuthService: SiteAuthenticationSettingsService;
}

export type AuthenticationSiteSettingsSubspaceProps = AuthenticationSiteSettingsSubspaceOwnProps & AuthenticationSiteSettingsSubspaceAngularProps;

export interface AuthenticationSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
  readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
}

export class AuthenticationSiteSettingsSubspaceComponent extends React.PureComponent<AuthenticationSiteSettingsSubspaceProps, AuthenticationSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  private initialState: AuthenticationSiteSettingsSubspaceState = {hasDataToRender: false, hasError: false, siteSettings: undefined, siteAuthenticationSettings: undefined};

  public constructor(props: AuthenticationSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: AuthenticationSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.siteSettings) && !_.isUndefined(this.state.siteAuthenticationSettings)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof AuthenticationSiteSettingsSubspaceState>(newState: Pick<AuthenticationSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getSiteSettings();
    this.getSiteAuthenticationSettings();
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteAuthenticationSettings = async () => {
    try {
      const siteAuthSettings = await this.props.siteAuthService.getSiteAuthenticationSettings(/* refresh */ true);
      this.updateState({siteAuthenticationSettings: siteAuthSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as any;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;
    childScope.siteAuthenticationSettings = this.state.siteAuthenticationSettings;
    childScope.siteSettingsForSiteAdmin = this.state.siteSettings as unknown as ISiteSettingsForSiteAdmin;
    this.childScope = childScope;
    return childScope;
  }
}

const AuthenticationSiteSettingsHOC = angularProviderHOC<AuthenticationSiteSettingsSubspaceOwnProps, AuthenticationSiteSettingsSubspaceAngularProps, AuthenticationSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServerSettingsInstance,
    sites: ServiceLocator.get<Sites>('Sites'),
    siteAuthService: ServiceLocator.get<SiteAuthenticationSettingsService>('SiteAuthenticationSettingsService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const AuthenticationSiteSettingsSubspace: React.ComponentType<AuthenticationSiteSettingsSubspaceOwnProps>
  = AuthenticationSiteSettingsHOC(AuthenticationSiteSettingsSubspaceComponent);
